import React, { Component } from 'react';
import { PropTypes } from 'prop-types';
import { connect } from 'react-redux';
import { get, debounce } from 'lodash';
import { Icon } from '@unite-us/ui';
import callOrLog from 'src/common/utils/callOrLog';
import { SEARCH } from 'common/utils/EventTracker/utils/eventConstants';
import { hasClientIdStandardization } from 'common/utils/FeatureFlags/flags';
import { fetchSearchResults } from 'actions/Search/Group';
import { goToSearchIndex } from './utils';
import SearchBarOverlay from './components/SearchBarOverlay';
import { MIN_CHAR_COUNT_FOR_SEARCH, MAX_CHAR_COUNT_FOR_SEARCH, MIN_CHAR_WARNING_TEXT } from '../constants';
import './stylesheets/searchBar.scss';

const composeAccessibilityMessage = (totalCount, query) => {
  const message = totalCount > 0 ?
          `Found ${totalCount} results${query ? ` for "${query}"` : ''}.` :
          `No results found${query ? ` for "${query}"` : ''}.`;
  return message;
};

export class SearchBar extends Component {
  constructor(props) {
    super(props);

    this.state = {
      query: '',
      searchResults: {},
      showResultsDropdown: false,
      accessibilityMessage: '',
      searchFailed: false,
    };

    this.clearSearchResults = this.clearSearchResults.bind(this);
    this.closeSearchResults = this.closeSearchResults.bind(this);
    this.fetchSearchResults = this.fetchSearchResults.bind(this);
    this.debouncedFetchSearchResults = debounce(this.fetchSearchResults, 500);
    this.handleInputChange = this.handleInputChange.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
  }

  getChildContext() {
    const {
      closeSearchResults,
      isFetching,
      session,
      clientIdStandardizationEnabled,
    } = this.props;

    const {
      query,
      searchResults,
      searchFailed,
    } = this.state;

    return {
      clientIdStandardizationEnabled,
      closeSearchResults,
      isFetching,
      query,
      searchResults,
      searchFailed,
      session,
    };
  }

  // eslint-disable-next-line react/sort-comp
  fetchSearchResults(query) {
    const { groupId } = this.context;
    const { networkId, isCC } = this.props;

    if (query.length < MIN_CHAR_COUNT_FOR_SEARCH) {
      return;
    }

    this.props.fetchSearchResults(
      {
        groupId,
        query,
        networkId,
        isCC,
      },
    )
      .then((searchResults) => {
        if (this.state.query.length < MIN_CHAR_COUNT_FOR_SEARCH) {
          return;
        }

        if (!searchResults?.contact) {
          this.setState({
            searchResults: {},
            accessibilityMessage: 'Search failed. Please try again.',
            searchFailed: true,
          });
          return;
        }

        const contactData = searchResults?.contact?.data ?? [];
        const totalCount = contactData.length;

        setTimeout(() => {
          this.setState({
            searchResults,
            accessibilityMessage: composeAccessibilityMessage(totalCount, query),
            searchFailed: false,
          });
        }, 300);
      })
      .catch(() => {
        if (this.state.query.length >= MIN_CHAR_COUNT_FOR_SEARCH) {
          this.setState({
            searchResults: {},
            accessibilityMessage: 'Search failed. Please try again.',
            searchFailed: true,
          });
        }
      });
  }

  clearSearchResults() {
    this.setState({
      searchResults: {},
      searchFailed: false,
    });
  }

  closeSearchResults() {
    this.props.closeSearchResults();
    callOrLog(() => this.context.eventTracker(SEARCH.closeSearchBar));
  }

  handleInputChange(event) {
    const query = event.target.value;

    const hasQuery = query.length > 0;
    const isSearchableQuery = query.length >= MIN_CHAR_COUNT_FOR_SEARCH;

    this.setState({ query }, () => {
      this.setState({ showResultsDropdown: hasQuery });

      if (isSearchableQuery) {
        this.debouncedFetchSearchResults(query);
        return;
      }

      this.clearSearchResults();
      this.setState({ accessibilityMessage: '', searchFailed: false });
    });
  }

  handleSubmit(e) {
    e.preventDefault();

    const { query } = this.state;

    if (query.length < MIN_CHAR_COUNT_FOR_SEARCH) {
      this.setState({ accessibilityMessage: MIN_CHAR_WARNING_TEXT });
      return;
    }

    this.setState({ accessibilityMessage: '' });
    this.props.closeSearchResults();
    callOrLog(() => this.context.eventTracker(SEARCH.searchIndex));
    goToSearchIndex({ query });
  }

  render() {
    return (
      <div className="search-bar">
        <div className="search-bar__form-container">
          <Icon
            icon="IconSearch"
            className="search-bar__button-search"
            color={'#FFFFFF'}
            size={20}
          />

          <form className="search-bar__form" onSubmit={this.handleSubmit}>
            <label htmlFor="search-bar__input-field" className="sr-only">Search</label>
            <input
              id="search-bar__input-field"
              className="search-bar__input"
              autoComplete="off"
              maxLength={MAX_CHAR_COUNT_FOR_SEARCH}
              autoFocus
              onChange={this.handleInputChange}
              placeholder="Search"
              type="search"
              value={this.state.query}
            />
            <div
              className="sr-only"
              role="status"
              aria-live="polite"
              aria-atomic="true"
            >
              {this.state.accessibilityMessage}
            </div>
          </form>

          <Icon
            className="search-bar__button-clear"
            color={'#FFFFFF'}
            icon="IconCross"
            onClick={this.closeSearchResults}
            size={18}
            ariaLabel="Close client search bar"
          />

          <SearchBarOverlay
            showResultsDropdown={this.state.showResultsDropdown}
          />
        </div>
      </div>
    );
  }
}

SearchBar.propTypes = {
  clientIdStandardizationEnabled: PropTypes.bool.isRequired,
  closeSearchResults: PropTypes.func.isRequired,
  fetchSearchResults: PropTypes.func.isRequired,
  isFetching: PropTypes.bool.isRequired,
  session: PropTypes.object.isRequired,
  networkId: PropTypes.string,
  isCC: PropTypes.bool,
};

SearchBar.contextTypes = {
  eventTracker: PropTypes.func.isRequired,
  groupId: PropTypes.string.isRequired,
};

SearchBar.childContextTypes = {
  clientIdStandardizationEnabled: PropTypes.bool.isRequired,
  closeSearchResults: PropTypes.func.isRequired,
  isFetching: PropTypes.bool.isRequired,
  query: PropTypes.string.isRequired,
  searchResults: PropTypes.object.isRequired,
  searchFailed: PropTypes.bool.isRequired,
  session: PropTypes.object.isRequired,
};

function mapStateToProps(state) {
  const clientIdStandardizationEnabled = hasClientIdStandardization(state);

  return {
    clientIdStandardizationEnabled,
    isFetching: get(state, 'search.isFetching', false),
    session: get(state, 'session', {}),
    networkId: get(state, 'session.networkId'),
    isCC: get(state, 'session.isCoordinationGroup', false),
  };
}

export default connect(
  mapStateToProps,
  { fetchSearchResults },
)(SearchBar);
